import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import React, { Dispatch, SetStateAction, useState } from 'react';

import CinemaPickerListItem from '@/components/CinemaPickerListItem';
import CinemaProfileImageRound from '@/components/CinemaProfileImageRound';

import { CinemaDto } from '@cineamo/api/models/cinema/CinemaDto.types';

import { getCinemas } from '@/utils/api/cinema/cinema-api';
import { CinemaApiEmbed } from '@cineamo/api/resources/cinema/cinema-api.embed';

import { constructCinemaAddress } from '@/helpers/cinema-helper';
import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconLocation24 } from '@cineamo/ui/src/icons/ic-location';
import { BasicInputStyle } from '@cineamo/ui/src/input/BasicInput';
import SearchBar from '@cineamo/ui/src/input/SearchBar';
import ListLoadingSpinner from '@cineamo/ui/src/loading/ListLoadingSpinner';
import TablePaginator from '@cineamo/ui/src/table/TablePaginator';

export type CinemaPickerDropdownProps = {
    open: boolean;
    setOpen: Dispatch<SetStateAction<boolean>>;
    currentCinema: CinemaDto;
    selectCinemaHandler?: (cinema: CinemaDto) => void;
};

function CinemaPickerDropdown(props: CinemaPickerDropdownProps): React.JSX.Element {
    const { open, currentCinema, selectCinemaHandler } = props;

    const t = useTranslations();
    const [query, setQuery] = useState<string | null>(null);
    const [page, setPage] = useState<number>(1);

    const {
        data: cinemasResponse,
        isLoading,
        isFetching
    } = useQuery({
        queryKey: ['cinemas', query, page],
        queryFn: async () =>
            getCinemas({
                embed: [CinemaApiEmbed.CINEMA_CONFIGURATION],
                ...(query ? { query } : {}),
                page: page
            }),
        enabled: open,
        refetchInterval: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchIntervalInBackground: false
    });

    const cinemas = cinemasResponse?._embedded?.cinemas || [];

    function constructAddress(currentCinema: CinemaDto): React.JSX.Element {
        return (
            <div className="flex flex-col justify-center font-montserrat-bold whitespace-nowrap text-ellipsis overflow-hidden mx-10">
                {currentCinema ? (
                    <>
                        <div className="whitespace-nowrap text-ellipsis overflow-hidden">{currentCinema?.name}</div>
                        <div className="whitespace-nowrap text-ellipsis overflow-hidden text-body-bold-16">
                            {constructCinemaAddress(currentCinema)}
                        </div>
                    </>
                ) : (
                    <span>{t('demo.cinema-picker.choose-cinema')}</span>
                )}
            </div>
        );
    }

    function onSearchCinema(query: string) {
        setQuery(query);
    }

    return (
        <div
            className={cn(
                open ? 'max-h-screen' : 'max-h-0',
                'fixed right-0 w-[85%] px-12',
                'md:absolute md:left-auto md:right-0 md:w-auto md:min-w-300 md:px-0',
                'transition-all duration-200 overflow-hidden'
            )}>
            <div className="flex flex-col w-full rounded-4 top-34 bg-white text-darkBlue shadow-2xl divide-gray/50 py-12 space-y-3">
                <div className="w-full flex flex-row px-16 items-center">
                    <CinemaProfileImageRound
                        cinema={currentCinema}
                        size="32"
                    />
                    {constructAddress(currentCinema)}
                    {currentCinema && <IconLocation24 className="flex-none ml-auto" />}
                </div>

                <div className="h-1 w-full bg-gray" />

                <SearchBar
                    className="mx-8"
                    basicInputProps={{
                        style: BasicInputStyle.gray_on_white_bg
                    }}
                    value={query ?? undefined}
                    placeholder={t('demo.cinema-picker.search-for-cinema')}
                    onChange={onSearchCinema}
                />

                <div className="flex flex-col h-full overflow-y-auto">
                    {!(isLoading || isFetching) &&
                        cinemas?.map((cinema) => (
                            <CinemaPickerListItem
                                key={cinema?.id}
                                className="w-full"
                                cinema={cinema}
                                onClick={selectCinemaHandler}
                            />
                        ))}

                    {!(isLoading || isFetching) && (!cinemasResponse || (cinemasResponse._total_items ?? 0) <= 0) && (
                        <span className="px-16">{t('demo.cinema-picker.no-cinema-found')}</span>
                    )}

                    {(isLoading || isFetching) && (
                        <ListLoadingSpinner
                            className="p-24"
                            dotClass="bg-darkBlue"
                        />
                    )}
                </div>

                {(cinemasResponse?._page_count ?? 0) > 1 && (
                    <TablePaginator
                        showRange={false}
                        page={page}
                        setPage={setPage}
                        pageCount={cinemasResponse?._page_count}
                    />
                )}
            </div>
        </div>
    );
}

export default CinemaPickerDropdown;
